import React from 'react';
import { useAuth } from '../../context/AuthContext';
import perfilImg from '../../assets/images/perfil.png'; // Asumiendo que aún se usa la imagen de perfil

import {
  FaWhatsapp, FaArrowRight, FaBrain, FaHeart, FaFistRaised, FaTools, FaLightbulb,
  FaRunning, FaCalendarAlt, FaFire, FaRegSurprise, FaUserAltSlash, FaInstagram, FaYoutube,
  FaCheckCircle, FaTimesCircle, FaSun, FaLeaf, FaSeedling, FaStar, FaQuestionCircle, FaHandsHelping, FaRocket
} from "react-icons/fa";
import { FiCompass } from "react-icons/fi";


// --- Datos para la página de Sesión de Coaching 1:1 ---

const benefits = [
  { text: "Identificar tus valores fundamentales y pasiones auténticas.", icon: <FaStar /> },
  { text: "Clarificar tu visión de vida y tus metas más profundas.", icon: <FaLightbulb /> },
  { text: "Desbloquear creencias limitantes que te impiden avanzar.", icon: <FaBrain /> },
  { text: "Diseñar un plan de acción concreto para iniciar tu camino con propósito.", icon: <FiCompass /> },
  { text: "Obtener claridad, dirección y la confianza para dar tus próximos pasos.", icon: <FaHandsHelping /> },
];

const processSteps = [
    { title: "Paso 1: Agendamos tu sesión", description: "Elige el horario que mejor te convenga. Es una sesión de 60 minutos 1:1.", icon: <FaCalendarAlt /> },
    { title: "Paso 2: Nos conectamos por videollamada", description: "En un espacio seguro y de confianza, profundizaremos en tu situación actual.", icon: <FaHeart /> },
    { title: "Paso 3: Descubrimos tu propósito", description: "A través de preguntas y herramientas, identificarás tus deseos más genuinos.", icon: <FaLightbulb /> },
    { title: "Paso 4: Diseñamos tu camino", description: "Saldrás con un plan claro y los primeros pasos para vivir alineado a tu esencia.", icon: <FaRocket /> },
];

const faqs = [
  { q: "¿Cuánto dura la sesión?", a: "La sesión individual tiene una duración aproximada de 60 minutos." },
  { q: "¿Necesito preparación previa?", a: "No, solo ven con la mente abierta y tu intención de explorar tu propósito." },
  { q: "¿Cómo se realiza la sesión?", a: "La sesión se lleva a cabo de forma virtual a través de videollamada (Zoom o similar)." },
  { q: "¿Qué pasa si no sé cuál es mi propósito?", a: "Ese es justamente el objetivo de la sesión: ayudarte a explorar, comprender y clarificar lo que realmente te mueve." },
  { q: "¿Cómo se efectúa el pago?", a: "El pago se coordina directamente por WhatsApp o tras agendar la sesión, utilizando métodos seguros." },
];

// --- Componentes reutilizables adaptados ---

const InViewSection = ({ title, children, index, id }) => (
  <section
    id={id}
    className="w-full max-w-lg mx-auto mb-12 p-6 rounded-xl border border-gray-700 bg-black/40 shadow-xl shadow-gray-900/50 fade-in-up"
    style={{ animationDelay: `${index * 150 + 700}ms` }}
  >
    {/* Título de sección ahora en tono azul/celeste */}
    <h3 className="text-2xl font-bold mb-5 text-center text-sky-300 border-b border-gray-700 pb-3">{title}</h3>
    {children}
  </section>
);

const CTAButtonsOneToOne = ({ delay }) => (
  <div
    className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-8 w-full max-w-md fade-in-up"
    style={{ animationDelay: `${delay}ms` }}
  >
    <a
      href={`https://wa.me/5491150254270?text=${encodeURIComponent("Hola Gustavo, quiero agendar mi Sesión 1:1 para encontrar mi propósito (USD $17).")}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center px-7 py-3 rounded-xl border border-green-600 bg-green-700/60 text-white font-bold text-lg shadow-lg shadow-green-700/40 hover:bg-green-600 hover:scale-105 transition-all duration-300 ease-in-out w-full sm:w-auto"
    >
      <FaWhatsapp className="mr-3 text-2xl" /> Quiero mi Sesión Ahora
    </a>

    <div className="flex flex-col items-center w-full sm:w-auto">
      <a
        href="https://calendly.com/gustavonahuelaguiar/30min" // Usamos el mismo Calendly para coordinar
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center px-7 py-3 rounded-xl border border-purple-600 bg-purple-700/60 text-white font-bold text-lg shadow-lg shadow-purple-700/40 hover:bg-purple-600 hover:scale-105 transition-all duration-300 ease-in-out w-full sm:w-auto"
      >
        <FaCalendarAlt className="mr-3 text-2xl" /> Agendar Sesión de Claridad
      </a>
      <span className="text-xs text-gray-400 mt-1">Coordinamos el pago y los detalles</span>
    </div>
  </div>
);

// --- Componente principal de la página ---
const OneToOneCoachingPage = () => {
  const { user } = useAuth();

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col relative overflow-hidden">
      <div className="fixed inset-0 bg-gradient-to-br from-gray-950 via-black to-gray-900 opacity-95 z-0 animate-gradient-shift"></div>

      {/* Estilos para animación de fondo y fade-in-up (copiados para ser autocontenidos) */}
      <style>
        {`
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradient-shift 15s ease infinite;
        }
        .fade-in-up {
          animation-fill-mode: both;
          animation-duration: 0.8s;
          animation-name: fade-in-up-keyframes;
          opacity: 0;
          transform: translateY(20px);
        }
        @keyframes fade-in-up-keyframes {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        `}
      </style>

      <main className="relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center">
        {/* HERO */}
        {/* Gradiente de azul profundo para el título principal */}
        <h1
          className="text-4xl sm:text-6xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-sky-300 via-blue-400 to-indigo-500 text-center leading-tight fade-in-up"
          style={{ animationDelay: '100ms' }}
        >
          Sesión 1:1: Encuentra tu Propósito
        </h1>

        <p
          className="text-2xl sm:text-4xl text-gray-100 text-center max-w-2xl mb-5 font-light fade-in-up"
          style={{ animationDelay: '250ms' }}
        >
          Diseñá la vida que siempre quisiste, paso a paso.
        </p>

        <p
          className="text-lg sm:text-xl text-gray-400 text-center max-w-xl mb-12 fade-in-up"
          style={{ animationDelay: '400ms' }}
        >
          Una sesión personalizada para desbloquear tu verdadero potencial y vivir con pasión.
        </p>

        {/* CTA inicial */}
        <div className="w-full max-w-lg mx-auto mb-12 flex justify-center fade-in-up" style={{ animationDelay: '550ms' }}>
          <CTAButtonsOneToOne delay={0} /> {/* Delay se maneja desde el padre */}
        </div>

        {/* Precio destacado */}
        <div
          className="w-full max-w-lg mx-auto mb-10 p-6 rounded-xl border-2 border-sky-500 bg-gradient-to-br from-black/50 to-gray-900/50 shadow-2xl shadow-blue-900/40 text-center fade-in-up"
          style={{ animationDelay: '700ms' }}
        >
            {/* Texto de inversión en tono celeste */}
            <h3 className="text-3xl sm:text-4xl font-extrabold text-sky-300 mb-2">
                Inversión en tu Claridad:
            </h3>
            <p className="text-5xl sm:text-6xl font-extrabold text-white mb-4">
                $17 <span className="text-2xl sm:text-3xl text-gray-400">USD</span>
            </p>
            <p className="text-lg text-gray-300">
                Sesión única de coaching 1:1 de 60 minutos.
            </p>
        </div>


        {/* Beneficios */}
        <InViewSection title="Lo que vas a lograr en tu sesión" index={0} id="beneficios">
          <ul className="list-none pl-0 text-gray-300 space-y-3">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-center">
                {/* Iconos de beneficios en tono celeste */}
                <span className="text-sky-400 mr-3 text-xl flex-shrink-0">{b.icon}</span>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
        </InViewSection>

        {/* Cómo funciona el proceso */}
        <InViewSection title="Nuestro camino juntos" index={1} id="como-funciona">
          <div className="space-y-6">
            {processSteps.map((step, i) => (
              <div key={i} className="flex items-start bg-gray-900/30 p-4 rounded-lg shadow-inner">
                {/* Iconos de pasos del proceso en tono índigo */}
                <span className="text-indigo-400 mr-4 text-2xl flex-shrink-0">{step.icon}</span>
                <div>
                  {/* Títulos de pasos del proceso en tono celeste */}
                  <p className="font-bold text-sky-300 mb-1">{step.title}</p>
                  <p className="text-gray-200 text-sm sm:text-base">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </InViewSection>

        {/* Para quién es */}
        <InViewSection
          title={
            <span className="flex items-center justify-center gap-2">
              <FaCheckCircle className="text-green-500 text-2xl" />
              <span>Esta sesión es para vos si...</span>
            </span>
          }
          index={2}
          id="para-quien"
        >
          <ul className="list-none pl-0 text-gray-200 space-y-2">
            <li><span className="text-green-400 mr-2 text-lg">✔</span> Te sentís desorientado/a y buscás claridad en tu vida.</li>
            <li><span className="text-green-400 mr-2 text-lg">✔</span> Querés descubrir lo que realmente te apasiona y te motiva.</li>
            <li><span className="text-green-400 mr-2 text-lg">✔</span> Necesitás un impulso para empezar a vivir con mayor sentido.</li>
            <li><span className="text-green-400 mr-2 text-lg">✔</span> Estás dispuesto/a a invertir en tu crecimiento personal y autoconocimiento.</li>
          </ul>
        </InViewSection>

        {/* No es para vos */}
        <InViewSection
          title={
            <span className="flex items-center justify-center gap-2">
              <FaTimesCircle className="text-red-500 text-2xl" />
              <span>NO es para vos si...</span>
            </span>
          }
          index={3}
        >
          <ul className="list-none pl-0 text-gray-200 space-y-2">
            <li><span className="text-red-400 mr-2 text-lg">✖</span> Buscás soluciones rápidas sin compromiso personal.</li>
            <li><span className="text-red-400 mr-2 text-lg">✖</span> Preferís que otros te digan qué hacer en lugar de descubrirlo vos mismo/a.</li>
            <li><span className="text-red-400 mr-2 text-lg">✖</span> No estás abierto/a a explorar nuevas perspectivas sobre tu vida.</li>
          </ul>
        </InViewSection>

        {/* Sobre mí (adaptado) */}
        <InViewSection title="Tu guía en este proceso:" index={4} id="sobre-mi">
          <img
            src={user?.profileImage || perfilImg}
            alt="Perfil de Gustavo Aguiar"
            // Borde de imagen en tono azul
            className="w-32 h-32 sm:w-40 sm:h-40 rounded-full mb-5 object-cover border-4 border-blue-500 shadow-xl mx-auto transform hover:scale-105 transition-transform duration-300"
          />
          {/* Nombre de Gustavo Aguiar en tono celeste */}
          <h2 className="text-xl sm:text-3xl font-bold mb-2 text-center text-sky-300">Gustavo Aguiar</h2>
          <p className="text-sm sm:text-base text-gray-300 text-center max-w-sm mx-auto leading-relaxed">
            Como Coach Holístico, mi misión es acompañarte a reconectar con tu esencia y manifestar una vida llena de propósito. Juntos, crearemos el camino hacia tu claridad.
          </p>
        </InViewSection>

        {/* FAQs */}
        <InViewSection title="Preguntas frecuentes" index={5} id="faqs">
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <div key={i} className="bg-gray-900/30 p-4 rounded-lg shadow-inner">
                {/* Títulos de FAQ en tono celeste */}
                <p className="font-bold text-sky-300 mb-1 flex items-center">
                  <span className="mr-2 text-xl"><FaQuestionCircle /></span> {f.q}
                </p>
                <p className="text-gray-200">{f.a}</p>
              </div>
            ))}
          </div>
        </InViewSection>

        {/* CTA final */}
        <div className="w-full max-w-lg mx-auto mb-16 flex justify-center fade-in-up" style={{ animationDelay: '1500ms' }}>
          <CTAButtonsOneToOne delay={0} />
        </div>
      </main>

      {/* FOOTER */}
      <footer
        className="relative z-10 text-center py-8 text-xs text-gray-500 bg-black/50 border-t border-gray-800 fade-in-up"
        style={{ animationDelay: '1700ms' }}
      >
        <div className="flex justify-center space-x-6 mb-3">
          <a href="https://instagram.com/ingeniero.consciente" target="_blank" rel="noopener noreferrer" aria-label="Visita mi Instagram">
            {/* Icono de Instagram con hover en tono celeste */}
            <FaInstagram className="text-gray-400 hover:text-sky-500 text-2xl transition-colors duration-300" />
          </a>
        </div>
        <p>© {new Date().getFullYear()} Gustavo Aguiar | Encontrá tu propósito.</p>
        <p className="mt-1">Todos los derechos reservados.</p>
      </footer>
    </div>
  );
};

export default OneToOneCoachingPage;